import { motion } from 'motion/react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { useI18n } from '../../i18n';
import LanguageSwitcher from './LanguageSwitcher';

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useI18n();

  const basePath = `/${language}`;

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== basePath) {
      navigate(`${basePath}#${sectionId}`);
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.header
      className={`fixed top-0 right-0 left-0 z-50 px-4 py-4 transition-colors duration-300 sm:px-6 sm:py-6 md:px-8 lg:px-32 lg:py-8 ${'border-b border-gray-100/50 bg-white/95 backdrop-blur-md'}`}
      initial={{ y: -100, opacity: 0 }}
      animate={{
        y: 0,
        opacity: 1,
      }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <nav className="flex items-center justify-between">
        <div className="flex items-center gap-4 xl:gap-8">
          <motion.div
            className="flex items-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              delay: 0.3,
              duration: 0.5,
              type: 'spring',
              stiffness: 200,
            }}
          >
            <Link to={basePath}>
              <img
                src="/images/HospitalLogo.png"
                alt={t.hero.brandName}
                width={1280}
                height={1280}
                fetchPriority="high"
                className="h-28 w-auto object-contain sm:-my-8 sm:h-32 lg:-my-16 lg:h-52"
              />
            </Link>
          </motion.div>
          <motion.div
            className="hidden items-center gap-4 lg:flex xl:gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            {t.nav.items.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 + index * 0.1, duration: 0.4 }}
              >
                {item.isSection ? (
                  <motion.button
                    onClick={() => scrollToSection(item.id)}
                    className="text-lg font-normal text-black transition-colors hover:text-green-600 xl:text-xl"
                    whileHover={{ scale: 1.05, color: '#00a63e' }}
                  >
                    {item.title}
                  </motion.button>
                ) : (
                  <Link to={`${basePath}/${item.id}`}>
                    <motion.span
                      className="text-lg font-normal text-black transition-colors hover:text-green-600 xl:text-xl"
                      whileHover={{ scale: 1.05, color: '#00a63e' }}
                    >
                      {item.title}
                    </motion.span>
                  </Link>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>

        <motion.div
          className="hidden items-center gap-3 md:gap-4 lg:flex lg:gap-6"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <LanguageSwitcher />
          <motion.a
            href="https://customer.medhub.tgl.com.sa"
            target="_blank"
            rel="noopener"
            className="rounded-md bg-green-600 px-2 py-2 text-sm font-bold text-white transition-colors hover:bg-green-700 sm:px-3 md:px-4 md:text-base"
            whileHover={{ scale: 1.05, backgroundColor: '#007a30' }}
            whileTap={{ scale: 0.95 }}
          >
            {t.nav.customerButton}
          </motion.a>
          <motion.a
            href="https://provider.medhub.tgl.com.sa"
            target="_blank"
            rel="noopener"
            className="rounded-md border border-gray-300 px-2 py-2 text-sm text-black transition-colors hover:bg-gray-50 sm:px-3 md:px-4 md:text-base"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t.nav.vendorButton}
          </motion.a>
        </motion.div>
        <motion.button
          className="flex flex-col gap-1 p-2 lg:hidden"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          whileTap={{ scale: 0.95 }}
        >
          <span
            className={`h-0.5 w-6 bg-black transition-all ${
              isMobileMenuOpen ? 'translate-y-1.5 rotate-45' : ''
            }`}
          ></span>
          <span
            className={`h-0.5 w-6 bg-black transition-all ${
              isMobileMenuOpen ? 'opacity-0' : ''
            }`}
          ></span>
          <span
            className={`h-0.5 w-6 bg-black transition-all ${
              isMobileMenuOpen ? '-translate-y-1.5 -rotate-45' : ''
            }`}
          ></span>
        </motion.button>
      </nav>

      <motion.div
        className={`absolute top-full right-0 left-0 bg-white shadow-lg lg:hidden ${
          isMobileMenuOpen ? 'block' : 'hidden'
        }`}
        initial={{ opacity: 0, y: -20 }}
        animate={{
          opacity: isMobileMenuOpen ? 1 : 0,
          y: isMobileMenuOpen ? 0 : -20,
        }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex flex-col items-start gap-2.5 px-6 py-10">
          <div className="flex w-full flex-col items-start gap-8">
            {t.nav.items.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ x: -20, opacity: 0 }}
                animate={{
                  x: isMobileMenuOpen ? 0 : -20,
                  opacity: isMobileMenuOpen ? 1 : 0,
                }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
                className="w-full"
              >
                {item.isSection ? (
                  <motion.button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      scrollToSection(item.id);
                    }}
                    className="block w-full text-end text-xl font-normal text-black transition-colors hover:text-green-600"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {item.title}
                  </motion.button>
                ) : (
                  <Link
                    to={`${basePath}/${item.id}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <motion.span
                      className="block text-end text-xl font-normal text-black transition-colors hover:text-green-600"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {item.title}
                    </motion.span>
                  </Link>
                )}
              </motion.div>
            ))}
          </div>

          <div className="mt-4 flex w-full justify-center">
            <LanguageSwitcher />
          </div>

          <div className="mt-2 flex w-full flex-col gap-4 lg:hidden">
            <motion.a
              href="https://provider.medhub.tgl.com.sa"
              target="_blank"
              rel="noopener"
              className="flex h-11 w-full items-center justify-center rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-center text-base font-normal text-black transition-colors hover:bg-gray-50"
              whileTap={{ scale: 0.98 }}
              whileHover={{ scale: 1.02 }}
            >
              {t.nav.vendorButton}
            </motion.a>
            <motion.a
              href="https://customer.medhub.tgl.com.sa"
              target="_blank"
              rel="noopener"
              className="flex h-11 w-full items-center justify-center rounded-md bg-[#00a63e] px-3 py-2 text-center text-base font-bold text-[#FAFAFA] transition-colors hover:bg-[#008a34]"
              whileTap={{ scale: 0.98 }}
              whileHover={{ scale: 1.02 }}
            >
              {t.nav.customerButton}
            </motion.a>
          </div>
        </div>
      </motion.div>
    </motion.header>
  );
}
